import { useState, useEffect } from "react";
import {
  Shield, Wifi, WifiOff, BarChart3, Gauge,
  ClipboardCheck, ClipboardList, Star, MessageSquare, Mail, Phone, Cpu
} from "lucide-react";

const LEGEND = [
  { Icon: Cpu,            color: "#94a3b8", label: "AI summary" },
  { Icon: Phone,          color: "#facc15", label: "Call logged" },
  { Icon: Mail,           color: "#f59e0b", label: "Email waiting" },
  { Icon: MessageSquare,  color: "#ff5c5c", label: "SMS waiting" },
  { Icon: ClipboardCheck, color: "#34d399", label: "Scanned" },
  { Icon: ClipboardList,  color: "#f59e0b", label: "Not scanned" },
  { Icon: Star,           color: "#38bdf8", fill: "#38bdf8", label: "Next ticket" },
];

export function HeaderBar({ tickets, shopOpen, connected, activeCount, metrics = {}, redCount, yellowCount, greenCount }) {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const fmt = time.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit",
    timeZone: "America/Chicago",
  });

  const red = redCount ?? tickets.filter(t => t.priority_color === "red" && !t.is_critical).length;
  const yellow = yellowCount ?? tickets.filter(t => t.priority_color === "yellow").length;
  const green = greenCount ?? tickets.filter(t => t.priority_color === "green").length;
  const pressure = metrics.shop_pressure || "NORMAL";
  const pressureCfg = {
    NORMAL:  { label: "Normal",          color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", dot: "bg-emerald-400" },
    BUSY:    { label: "Busy",            color: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/20",   dot: "bg-amber-400" },
    BACKLOG: { label: "Backlog Forming", color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/20",     dot: "bg-red-400 blink" },
  };
  const pc = pressureCfg[pressure] || pressureCfg.NORMAL;

  return (
    <header
      data-testid="header-bar"
      className="w-full flex flex-col border-b border-white/[0.08] bg-[#020617]/90 backdrop-blur-md shrink-0 z-50"
    >
      {/* Main header row */}
      <div className="h-14 flex items-center justify-between px-5">
        {/* Left: Logo + Status */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" strokeWidth={2} />
            <span className="font-display font-bold text-lg tracking-wider text-slate-100">PROTEK</span>
          </div>
          <div className="h-5 w-px bg-white/10" />
          <span className="font-display text-sm text-slate-400">{fmt}</span>
          <div className="h-5 w-px bg-white/10" />
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${
            shopOpen ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-700/50 text-slate-400"
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${shopOpen ? "bg-emerald-400 blink" : "bg-slate-500"}`} />
            {shopOpen ? "OPEN" : "OFF SHIFT"}
          </div>
        </div>

        {/* Center: Shop Pressure */}
        <div data-testid="shop-pressure" className={`flex items-center gap-2 px-3 py-1 rounded border ${pc.bg} ${pc.border}`}>
          <Gauge className={`w-3.5 h-3.5 ${pc.color}`} strokeWidth={2} />
          <div className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
          <span className={`text-xs font-semibold tracking-wide ${pc.color}`}>{pc.label}</span>
        </div>

        {/* Right: Counts + Metrics + Connection */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 font-display text-sm">
            <span className="text-slate-400">Tickets: <span className="text-slate-200 font-semibold">{red + yellow + green}</span></span>
            <div className="flex items-center gap-0.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-red-400 font-semibold">{red}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-yellow-400 font-semibold">{yellow}</span>
            </div>
            <div className="flex items-center gap-0.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-emerald-400 font-semibold">{green}</span>
            </div>
          </div>
          {(metrics.oldest_ticket_days > 0 || metrics.avg_shop_days > 0) && (
            <>
              <div className="h-5 w-px bg-white/10" />
              <div data-testid="board-metrics" className="flex items-center gap-2 font-display text-[11px]">
                <BarChart3 className="w-3 h-3 text-slate-500" strokeWidth={2} />
                <span className="text-slate-500">Oldest: <span className={`font-semibold ${metrics.oldest_ticket_days >= 30 ? "text-red-400" : "text-slate-300"}`}>{metrics.oldest_ticket_days}d</span></span>
                <span className="text-slate-500">Avg: <span className="text-slate-300 font-semibold">{metrics.avg_shop_days}d</span></span>
              </div>
            </>
          )}
          <div className="h-5 w-px bg-white/10" />
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold ${
            connected ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
          }`}>
            <div className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 live-pulse" : "bg-red-500 blink"}`} />
            {connected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            <span className="tracking-wider">{connected ? "LIVE" : "OFFLINE"}</span>
          </div>
        </div>
      </div>

      {/* Legend strip */}
      <div data-testid="icon-legend" className="flex items-center justify-center gap-4 px-5 py-1 border-t border-white/[0.04] bg-slate-900/40">
        {LEGEND.map(({ Icon, color, fill, label }) => (
          <div key={label} className="flex items-center gap-1">
            <Icon className="w-3 h-3" style={{ color, fill: fill || "none" }} strokeWidth={2} />
            <span className="text-[10px] text-slate-500">{label}</span>
          </div>
        ))}
      </div>
    </header>
  );
}
